import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { markResultsSeen } from '../hooks/useMemberDashboard';
import { summariseLatestResultsBatch } from '../utils/dashboard/newResults';
import { listMemberVisibleLabResults } from '../services/observationService';
import { PageHeader } from '../components/layout/memberPrimitives';
import DataUploadsPage from './DataUploadsPage';

/**
 * /results — the member's blood work, as a destination of its own.
 *
 * This surface existed before, but only as `/intelligence?tab=results`: a query
 * param on a tab, inside a page called "Melio Intelligence", whose default tab
 * was the reading list. `/results` itself was a redirect *into* that. A member
 * whose first panel had just come back had no name to look for and no slot in
 * the rail to press, which is the feedback this page answers.
 *
 * The relationship is now inverted — `/results` is the real page and the old
 * tab links redirect out to it — so there is exactly one place results live and
 * one URL that means "my results".
 *
 * The body is `DataUploadsPage embedded`, which is now organised around the
 * draw — panel · date · lab, then the member's markers, with uploading and
 * manual entry collapsed underneath. That page keeps its name because it still
 * owns the upload and entry writes; what it leads with has changed.
 *
 * Opening this page is also what acknowledges the "your results are back" hero
 * on Home. The read is deliberately separate from the one inside the body: the
 * acknowledgement has to survive the body failing to render, or a member could
 * be nudged to a page that then could not clear the nudge.
 */
export default function MemberResultsPage() {
  const { currentUser } = useAuth();

  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid) return;
    let active = true;
    (async () => {
      try {
        const labs = await listMemberVisibleLabResults(uid);
        // Sex only affects which range a marker is judged against, and the
        // watermark is a timestamp — so it plays no part here and is not read.
        const batch = summariseLatestResultsBatch(labs, null);
        if (active && batch) markResultsSeen(uid, batch.observedAt);
      } catch {
        // Failing to record the visit costs a repeated nudge, nothing more.
      }
    })();
    return () => {
      active = false;
    };
  }, [currentUser]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 melio-stagger">
      <PageHeader
        breadcrumb={[{ label: 'Home', href: '/home' }, { label: 'Results' }]}
        eyebrow="Your results"
        title="Your results."
        subtitle="Every biomarker on file, where each one sits against your reference range, and the documents behind them."
        rightSlot={
          <Link
            to="/intelligence"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border melio-lift"
            style={{ borderColor: 'var(--m-line)', backgroundColor: 'var(--m-surface)', color: 'var(--m-ink)' }}
          >
            What this means →
          </Link>
        }
      />
      <DataUploadsPage embedded />
    </div>
  );
}
